import { getTheme } from '@fluentui/react';
import { ContextualMenuItemType, IContextualMenuItem, IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { FocusZoneDirection } from '@fluentui/react/lib/FocusZone';

import { getBoundingWindowWithPaddingFabric } from '@1js/window-util';

import {
  getMenuCalloutStyles,
  getMenuClassNames,
  getMenuSectionClassNames,
  getMenuTitleClassNames,
} from './Menu.classNames';
import { MenuSection } from './MenuDefinition';
import { MenuDefinitionToFabricMenuParameters } from './MenuDefinitionToFabricMenuParameters';

function convertMenuSectionToFabricMenuItems(
  menuSection: MenuSection,
  index: number,
  menuParameters: MenuDefinitionToFabricMenuParameters,
  renderDivider: boolean
): IContextualMenuItem {
  const {
    controlInMenuRendererFunction,
    location,
    onSubMenuPositioned,
    subComponentStyles,
    arePerfImprovementsEnabled,
    onMenuPositioned,
    onMenuItemExecuted,
  } = menuParameters;

  return {
    key: `section-${index}`,
    itemType: ContextualMenuItemType.Section,
    getItemClassNames: getMenuSectionClassNames(subComponentStyles),
    itemProps: {
      lang: menuSection.lang,
    },
    sectionProps: {
      // Only create a menu section title item if it is not empty.
      title:
        !isSectionEmpty(menuSection) && menuSection.title
          ? {
              text: menuSection.title,
              key: `section-${index}-title`,
              itemType: ContextualMenuItemType.Header,
              itemProps: {
                lang: menuSection.lang,
              },
            }
          : undefined,
      bottomDivider: renderDivider,
      items: controlInMenuRendererFunction({
        controlProps: menuSection.controls,
        location,
        onSubMenuPositioned,
        subComponentStyles,
        arePerfImprovementsEnabled,
        onMenuPositioned,
        onMenuItemExecuted,
      }),
    },
  };
}

function isSectionEmpty(menuSection: MenuSection): boolean {
  return menuSection.controls.length === 0;
}

export function convertMenuDefinitionToFabricMenuItems(
  menuParameters: MenuDefinitionToFabricMenuParameters,
  shouldHideDivider?: (pos: number, maxPos: number) => boolean
): IContextualMenuItem[] {
  let items: IContextualMenuItem[] = [];
  const { menuDefinition } = menuParameters;

  if (!menuDefinition) {
    return [];
  }

  if (menuDefinition.title) {
    items = items.concat({
      name: menuDefinition.title,
      key: 'menu-title',
      itemType: ContextualMenuItemType.Header,
      getItemClassNames: getMenuTitleClassNames,
    });
  }

  menuDefinition.sections.forEach((menuSection: MenuSection, index: number) => {
    // If this isn't the last section, the next section is not empty (has untrimmed controls), render a divider.
    const renderDivider =
      index < menuDefinition.sections.length - 1 &&
      !isSectionEmpty(menuDefinition.sections[index + 1]) &&
      !(shouldHideDivider && shouldHideDivider(index, menuDefinition.sections.length - 1));

    items = items.concat(convertMenuSectionToFabricMenuItems(menuSection, index, menuParameters, renderDivider));
  });

  return items;
}

export function convertMenuDefinitionToFabricMenuProps(
  menuParameters: MenuDefinitionToFabricMenuParameters,
  directionalHintFixed: boolean = true
): IContextualMenuProps {
  const { menuDefinition, customMenuClassNames, onMenuPositioned } = menuParameters;
  const props = (menuDefinition && menuDefinition.focusZoneProps) || {
    direction: FocusZoneDirection.bidirectional,
    checkForNoWrap: true,
    'data-no-horizontal-wrap': 'true',
  };
  const theme = menuParameters.theme || getTheme();
  return {
    items: convertMenuDefinitionToFabricMenuItems(menuParameters),
    isBeakVisible: false,
    directionalHintFixed,
    focusZoneProps: { ...props },
    getMenuClassNames: getMenuClassNames(customMenuClassNames),
    ariaLabel: menuDefinition ? menuDefinition.ariaLabel : '',
    calloutProps: {
      minPagePadding: 0,
      styles: getMenuCalloutStyles(theme, customMenuClassNames?.subComponentStyles?.callout),
      onPositioned: (): void => onMenuPositioned && onMenuPositioned(menuDefinition),
    },
    // any is needed due to fabric bug: https://github.com/OfficeDev/office-ui-fabric-react/issues/10517
    bounds: getBoundingWindowWithPaddingFabric as any,
  };
}

export function convertMenuDefinitionToFabricSubMenuProps(
  menuParameters: MenuDefinitionToFabricMenuParameters,
  subMenuId?: string
): IContextualMenuProps {
  const { menuDefinition, onSubMenuPositioned } = menuParameters;
  const theme = menuParameters.theme || getTheme();
  return {
    ...convertMenuDefinitionToFabricMenuProps(menuParameters, undefined /**directionHintFixed */),
    directionalHintFixed: undefined,
    calloutProps: {
      styles: getMenuCalloutStyles(theme),
      onPositioned: (): void => onSubMenuPositioned && onSubMenuPositioned(menuDefinition, subMenuId),
    },
    id: subMenuId + 'MenuId',
  };
}
